import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { requirePermission } from '@/utils/auth'
import { getAllVehiclesOptions } from '@/api/main'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Suspense, useCallback, useEffect } from 'react'
import { RouteLoading } from '@/components/Common/RouteLoader'
import { AutoScheduleModal } from '@/components/Scheduling/AutoSchedule/AutoScheduleModal'
import type { AutoScheduleTabId } from '@/utils/autoScheduleFlags'
import i18next from 'i18next'

const MODES: AutoScheduleTabId[] = ['requirements', 'districts', 'copy']

export type AutoScheduleSearch = {
  intervalStart?: string
  intervalEnd?: string
  mode?: AutoScheduleTabId
}

const parseMode = (value: unknown): AutoScheduleTabId | undefined =>
  typeof value === 'string' && (MODES as string[]).includes(value) ? (value as AutoScheduleTabId) : undefined

export const Route = createFileRoute('/scheduling/$startDate/$endDate/auto-schedule/$vehicleId')({
  beforeLoad: () => requirePermission('scheduling', 'write'),
  validateSearch: (raw: Record<string, unknown>): AutoScheduleSearch => {
    const search: AutoScheduleSearch = {}
    if (typeof raw.intervalStart === 'string' && raw.intervalStart !== '') {
      search.intervalStart = raw.intervalStart
    }
    if (typeof raw.intervalEnd === 'string' && raw.intervalEnd !== '') {
      search.intervalEnd = raw.intervalEnd
    }
    const mode = parseMode(raw.mode)
    if (mode) {
      search.mode = mode
    }
    return search
  },
  component: AutoScheduleRoute,
})

function AutoScheduleRoute() {
  return (
    <Suspense fallback={<RouteLoading title={i18next.t('loading', { ns: 'common' })} />}>
      <AutoScheduleRouteContent />
    </Suspense>
  )
}

function AutoScheduleRouteContent() {
  const { startDate, endDate, vehicleId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { data: vehicles } = useSuspenseQuery(getAllVehiclesOptions())

  const close = useCallback(() => {
    navigate({
      to: '/scheduling/$startDate/$endDate',
      params: { startDate, endDate },
    })
  }, [navigate, startDate, endDate])

  const vehicleExists = vehicles.some(vehicle => vehicle.id === vehicleId)

  useEffect(() => {
    if (!vehicleExists) {
      close()
    }
  }, [vehicleExists, close])

  if (!vehicleExists) {
    return null
  }

  const viewStart = decodeURIComponent(startDate)
  const viewEnd = decodeURIComponent(endDate)

  return (
    <AutoScheduleModal
      vehicleId={vehicleId}
      viewStart={viewStart}
      viewEnd={viewEnd}
      initialInterval={
        search.intervalStart && search.intervalEnd
          ? { start: search.intervalStart, end: search.intervalEnd }
          : undefined
      }
      initialMode={search.mode}
      onClose={close}
      onModeChange={mode =>
        navigate({
          to: '/scheduling/$startDate/$endDate/auto-schedule/$vehicleId',
          params: { startDate, endDate, vehicleId },
          search: previous => ({ ...previous, mode }),
        })
      }
    />
  )
}
